import { Grid, Skeleton } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys, useLenderDeclineReasonsQuery } from '../../shared/hooks';
import { DataRoomTeaser } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import * as Yup from 'yup';

interface TeaserResponseFormProps {
	dataRoomGuid: string | undefined;
	teaser: DataRoomTeaser | null;
	close: () => void;
}

export const TeaserResponseForm: React.FC<TeaserResponseFormProps> = ({ dataRoomGuid, teaser, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const declineReasonsQuery = useLenderDeclineReasonsQuery();
	const [showOther, setShowOther] = React.useState<boolean>(false);

	const onUpdateDeclineView = (selections: number[]): void => {
		setShowOther(selections.filter(num => num == -1).length > 0);
	};

	const responseMutation = useMutation(
		async ({ values }: TeaserResponseFormMutationVariables) => {
			const teaserPatch = {
				isAccepted: values.isAccepted,
				reasons: values.reasons,
				otherReason: values.otherReason,
				reasonDetail: values.reasonDetail,
				reEngagePoint: values.reEngagePoint,
			};

			const { data, error } = await getApiResponseData<DataRoomTeaser>(
				api.patch(CerebroApiEndpoints.Teasers.Specific(dataRoomGuid, teaser?.guid), teaserPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (data: DataRoomTeaser | null) => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('Teaser was updated.', { variant: 'success' });
				close();

				if (data?.reconciliationUrl) {
					snackbar.enqueueSnackbar('Navigating to deal criteria reconciliation...', { variant: 'info' });
					setTimeout(() => {
						window.location.href = data.reconciliationUrl as string;
					}, 1500);
				}
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={teaser != null} onClose={() => close()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				{teaser?.firstName} {teaser?.lastName}
			</CerebroHeading>
			<Formik
				validationSchema={TeaserResponseFormSchema}
				initialValues={{
					isAccepted:
						!teaser?.decisionStatus ||
						teaser.decisionStatus === 'Accepted' ||
						teaser.decisionStatus === 'Sent' ||
						teaser.decisionStatus === 'Unsent',
					reasons: [] as number[],
					otherReason: '',
					reasonDetail: '',
					reEngagePoint: '',
				}}
				onSubmit={FormikUtils.MutateOnSubmit(responseMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="isAccepted">Accept Teaser</CerebroLabel>
									<Field
										id="isAccepted"
										as={CerebroYesNo}
										name="isAccepted"
										disabled={isSubmitting}
									/>
								</Grid>

								{!values.isAccepted && (
									<>
										<Grid item xs={8}>
											<CerebroLabel id="reasons" required>
												Select Decline Reason(s)
											</CerebroLabel>
											{declineReasonsQuery.isLoading && <Skeleton height={60} />}
											{declineReasonsQuery.isSuccess && (
												<>
													<Field
														id="reasons"
														as={CerebroMultiDropdown}
														name="reasons"
														onClose={() => onUpdateDeclineView(values.reasons)}
														disabled={isSubmitting}
													>
														{declineReasonsQuery.data.map(reason => (
															<CerebroMultiDropdownMenuItem
																key={reason.guid}
																value={reason.id}
															>
																{reason.name}
															</CerebroMultiDropdownMenuItem>
														))}
													</Field>
													{FormikUtils.IsInvalid('reasons', errors, touched) && (
														<CerebroText variant="error">{errors.reasons}</CerebroText>
													)}
												</>
											)}
										</Grid>

										{showOther && (
											<Grid item xs={8}>
												<Field
													id="otherReason"
													name="otherReason"
													as={CerebroInput}
													placeholder="Other reason"
												/>
											</Grid>
										)}
										<Grid item xs={8}>
											<CerebroLabel id="reasonDetail" required>
												Details of Reason
											</CerebroLabel>
											<Field
												id="reasonDetail"
												as={CerebroTextarea}
												name="reasonDetail"
												disabled={isSubmitting}
											/>
											{FormikUtils.IsInvalid('reasonDetail', errors, touched) && (
												<CerebroText variant="error">{errors.reasonDetail}</CerebroText>
											)}
										</Grid>
										<Grid item xs={8}>
											<CerebroLabel id="reEngagePoint" required>
												Re-Engage Point
											</CerebroLabel>
											<Field
												id="reEngagePoint"
												as={CerebroTextarea}
												name="reEngagePoint"
												disabled={isSubmitting}
											/>
											{FormikUtils.IsInvalid('reEngagePoint', errors, touched) && (
												<CerebroText variant="error">{errors.reEngagePoint}</CerebroText>
											)}
										</Grid>
									</>
								)}
								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type TeaserResponseFormMutationVariables = FormikUtils.MutateOnSubmitVariables<TeaserResponseFormValues>;
interface TeaserResponseFormValues {
	isAccepted: boolean;
	reasons: number[];
	otherReason: string;
	reasonDetail: string;
	reEngagePoint: string;
}
const TeaserResponseFormSchema = Yup.object().shape({
	reEngagePoint: Yup.string().when('isAccepted', {
		is: false,
		then: Yup.string().required('Re-Engage Point is required.'),
		otherwise: Yup.string().notRequired(),
	}),
	reasonDetail: Yup.string().when('isAccepted', {
		is: false,
		then: Yup.string().required('Details of Reason is required.'),
		otherwise: Yup.string().notRequired(),
	}),
	reasons: Yup.array().when('isAccepted', {
		is: false,
		then: Yup.array().min(1, 'At least one reason must be selected.').required('Select Decline Reason(s).'),
		otherwise: Yup.array().notRequired(),
	}),
});
